import type { Action } from '@shared/helpers/pipeline/update-engine.models';
import type { Baseline } from '@shared/models/project.model';

/**
 * Whether two action lists contain exactly the same ids (same length, no duplicates, same set).
 * Used to detect roster-preserving patches (e.g. renames) vs append/delete batches.
 */
const isSameActionIdRoster = (previous: Action[], next: Action[]): boolean => {
  if (previous.length !== next.length) return false;
  const prevIds = new Set(previous.map((action) => action.id));
  if (prevIds.size !== previous.length) return false;
  for (const action of next) {
    if (!prevIds.has(action.id)) return false;
  }
  return true;
};

/**
 * Merges two sequential `actions` patches for the same building version.
 * When both lists share the same action ids, keeps the first list's order and overlays
 * action objects from the second so a rename does not replace the whole array reference
 * semantics from an earlier partial in the same batch.
 *
 * @param previous - Actions from the accumulated partial (earlier changes)
 * @param next - Actions from the next partial (later changes)
 * @returns Merged actions array for {@link Baseline}
 */
export const mergeVersionActionsPartials = (
  previous: Action[],
  next: Action[],
): Action[] => {
  if (isSameActionIdRoster(previous, next)) {
    const nextById = new Map(next.map((action) => [action.id, action]));
    // Preserve roster order from the first partial; apply per-id updates from `next`
    return previous.map((action) => nextById.get(action.id) ?? action);
  }
  return next;
};

/**
 * Builds a minimal `actions` patch that renames one action by id, copying only the
 * touched slot so sibling actions keep their references.
 *
 * @param version - Current version (read-only)
 * @param actionId - {@link Action.id} to rename
 * @param name - New display name
 * @returns Patch or `undefined` when nothing changes
 */
export const pickActionRenamePatch = (
  version: Baseline,
  actionId: string,
  name: string,
): Pick<Baseline, 'actions'> | undefined => {
  const actions = version.actions;
  if (!actions?.length) return undefined;

  const index = actions.findIndex((action) => action.id === actionId);
  if (index < 0) return undefined;

  const target = actions[index];
  if (!target || target.name === name) return undefined;

  const next = actions.slice();
  next[index] = { ...target, name };
  return { actions: next };
};
